/*
 * @providesModule PaymentErrorModal
 *
 */

import SkillzImage from 'SkillzImage';
import {DialogV3} from 'DialogV3';
import {_m} from 'INTL';
import React from 'react';
import {View, Text, StyleSheet, TouchableOpacity, Linking} from 'react-native';
import ThemeOverrideProps from 'ThemeOverrideProps';
const {fontExtraBold, fontRegular} = ThemeOverrideProps.sheetStyles;

const DEFAULT_TITLE = _m('Uh Oh!');
const DEFAULT_BODY = _m("Your deposit didn't go through. Please try another payment method.");
const AEROPAY_DEFAULT_SUPPORT_EMAIL = 'support@aeropay.com';

type PaymentErrorModalProps = {
  errorTitle?: string;
  errorMessage?: string;
  onDismiss: () => void;
};

export function PaymentErrorModal({
  errorTitle = DEFAULT_TITLE,
  errorMessage = DEFAULT_BODY,
  onDismiss,
}: PaymentErrorModalProps) {
  const errorText = React.useMemo(() => {
    // If the error message contains the Aeropay support email, we want to show it as a link
    if (errorMessage?.includes(AEROPAY_DEFAULT_SUPPORT_EMAIL)) {
      const [beforeEmailLink, ...errorMessageWithoutEmail] = errorMessage?.split(
        AEROPAY_DEFAULT_SUPPORT_EMAIL
      ) ?? [''];
      const afterEmailLink = errorMessageWithoutEmail.join(AEROPAY_DEFAULT_SUPPORT_EMAIL);

      return (
        <Text style={styles.text}>
          {beforeEmailLink}
          <Text style={styles.link} onPress={handleAeropayEmailPress}>
            {' ' + AEROPAY_DEFAULT_SUPPORT_EMAIL}
          </Text>
          {afterEmailLink}
        </Text>
      );
    }

    return errorMessage;
  }, [errorMessage]);

  function handleAeropayEmailPress() {
    Linking.openURL('mailto:' + AEROPAY_DEFAULT_SUPPORT_EMAIL).catch((err) => {
      console.error('PaymentErrorModal: Error on trying to open email app.', err);
    });
  }

  return (
    <DialogV3 showCloseButton={true} onClose={onDismiss}>
      <View style={styles.container}>
        <SkillzImage
          source={'ic_warning_error_payment.png'}
          style={styles.icon}
          resizeMode={'contain'}
        />
        <Text style={styles.title}>{errorTitle}</Text>
        <Text style={styles.text}>{errorText}</Text>

        <TouchableOpacity style={styles.button} onPress={onDismiss}>
          <Text style={styles.buttonText}>{_m('OK')}</Text>
        </TouchableOpacity>
      </View>
    </DialogV3>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    width: 66,
    height: 67,
  },
  title: {
    color: '#070707',
    fontSize: 20,
    lineHeight: 24,
    textAlign: 'center',
    letterSpacing: -0.03,
    ...fontExtraBold,
    ...fontExtraBold,
    opacity: 0.9,
    marginTop: 30,
  },
  text: {
    textAlign: 'center',
    color: '#070707',
    fontSize: 13,
    lineHeight: 18,
    letterSpacing: -0.01,
    ...fontRegular,
    marginTop: 8,
  },
  button: {
    marginTop: 20,
    maxHeight: 43,
    backgroundColor: '#367BCE',
    alignSelf: 'stretch',
    flex: 1,
    borderRadius: 58,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 19,

    height: 43,
    borderWidth: 1,
    borderColor: '#367BCE',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    ...fontExtraBold,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  link: {
    color: '#4990e2',
    textDecorationLine: 'underline',
  },
});
